import { Map } from "immutable"

import { UNAUTHENTICATED } from "../../actions/action-types"
import { serialiseSearchCriteria } from "../paging/paging-reducer-utils"

const defaultFilterModelClass = Map

const noop = (something) => something

const defaultUpdateChangeModelFunction = noop
const defaultUpdateSearchFilterModelFunction = noop
const defaultCanCancelFunction = (changeModel, imageToUpload, workingModel) =>
    changeModel.size > 0 || imageToUpload !== undefined
const defaultCanSaveFunction = defaultCanCancelFunction
const defaultCanDeleteFunction = () => true
const defaultWorkingModelToUIFunction = noop
const defaultWorkingModelClass = Map
const defaultCreateModelClass = Map
const defaultState = {
    searchCriteria: "",
    isFilterActive: false,
    isFetchingItem: false,
    isFetchingList: false,
    isSavingItem: false,
    isDeletingItem: false,
    isEditMode: false,
    isCreateMode: false,
    activeId: null,
    canSave: false,
    canCancel: false,
    canDelete: false,
    workingFilterModel: new defaultFilterModelClass(),
    searchFilterModel: new Map(),
    imageToUpload: undefined,
    workingModel: new defaultWorkingModelClass(),
    updateModel: new Map(),
}

class PresentationReducerBuilder {
    _actionsToHandle = {
        ADD_NEW: "",
        UPDATE_IMAGE: "",
        UPDATE_MODEL: "",
        APPLY_MODEL_UPDATE: "",
        CANCEL_MODEL_UPDATE: "",
        CLEAR_FILTER_CRITERIA: "",
        UPDATE_FILTER_CRITERIA: "",
        SET_FILTER: "",
        ITEM_REQUEST: "",
        ITEM_RESPONSE: "",
        ITEM_CACHED_RESPONSE: "",
        ITEM_ERROR_RESPONSE: "",
        ITEM_DELETE_REQUEST: "",
        ITEM_DELETE_RESPONSE: "",
        ITEM_DELETE_ERROR_RESPONSE: "",
        ITEM_SAVE_RESPONSE: "",
        ITEM_SAVE_ERROR_RESPONSE: "",
        LIST_PAGE_REQUEST: "",
        LIST_SEARCH_REQUEST: "",
        LIST_RESPONSE: "",
        LIST_ERROR_RESPONSE: "",
        LIST_CACHED_RESPONSE: "",
        SET_EDIT_MODE: "",
    }

    _updateChangeModel = defaultUpdateChangeModelFunction
    _updateCreateModel = null
    _updateSearchFilterModel = defaultUpdateSearchFilterModelFunction
    _canCancel = defaultCanCancelFunction
    _canSave = defaultCanSaveFunction
    _canDelete = defaultCanDeleteFunction
    _workingFilterModelClass = defaultFilterModelClass
    _workingModelToUI = defaultWorkingModelToUIFunction
    _workingModelClass = defaultWorkingModelClass
    _createModelClass = defaultCreateModelClass
    _defaultState = defaultState

    actionsToHandle(actionsToHandle) {
        this._actionsToHandle = {
            ...this._actionsToHandle,
            ...actionsToHandle,
        }
        return this
    }

    updateChangeModel(updateModelFunction) {
        this._updateChangeModel = updateModelFunction
        return this
    }

    updateCreateModel(updateCreateModelFunction) {
        this._updateCreateModel = updateCreateModelFunction
        return this
    }

    updateSearchFilterModel(updateSearchFilterModelFunction) {
        this._updateSearchFilterModel = updateSearchFilterModelFunction
        return this
    }

    canCancel(canCancelFunction) {
        this._canCancel = canCancelFunction
        return this
    }

    canSave(canSaveFunction) {
        this._canSave = canSaveFunction
        return this
    }

    canDelete(canDeleteFunction) {
        this._canDelete = canDeleteFunction
        return this
    }

    workingFilterModelClass(workingFilterModelClass) {
        this._workingFilterModelClass = workingFilterModelClass
        return this
    }

    workingModelToUI(workingModelToUIFunction) {
        this._workingModelToUI = workingModelToUIFunction
        return this
    }

    workingModelClass(workingModelClass) {
        this._workingModelClass = workingModelClass
        return this
    }

    createModelClass(createModelClass) {
        this._createModelClass = createModelClass
        return this
    }

    defaultState(defaultState) {
        this._defaultState = {
            ...this._defaultState,
            ...defaultState,
        }
        return this
    }

    getUpdateModelFunction(isCreateMode) {
        return isCreateMode && this._updateCreateModel
            ? this._updateCreateModel
            : this._updateChangeModel
    }

    build() {
        const that = this
        return (state = that._defaultState, action) => {
            const { type, name, value, file, error } = action
            let { searchFilterModel, workingFilterModel } = state
            switch (type) {
                case that._actionsToHandle.ADD_NEW: {
                    const workingModel = new that._workingModelClass()
                    const updateModel = new that._createModelClass()
                    return {
                        ...state,
                        workingModel,
                        updateModel,
                        activeId: null,
                        isCreateMode: true,
                        canCancel: that._canCancel(
                            updateModel,
                            file,
                            workingModel
                        ),
                        canSave: that._canSave(updateModel, file, workingModel),
                    }
                }
                case that._actionsToHandle.UPDATE_IMAGE: {
                    let imagePreviewURL = null
                    if (file) {
                        imagePreviewURL = URL.createObjectURL(file)
                    }
                    const previousPreviewURL =
                        state.workingModel.imagePreviewURL
                    if (previousPreviewURL) {
                        URL.revokeObjectURL(previousPreviewURL)
                    }
                    const workingModel = state.workingModel.set(
                        "imagePreviewURL",
                        imagePreviewURL
                    )

                    return {
                        ...state,
                        workingModel,
                        imageToUpload: file,
                        canCancel: that._canCancel(
                            state.updateModel,
                            file,
                            workingModel
                        ),
                        canSave: that._canSave(
                            state.updateModel,
                            file,
                            workingModel
                        ),
                    }
                }
                case that._actionsToHandle.UPDATE_MODEL: {
                    const updateFunction = that.getUpdateModelFunction(
                        state.isCreateMode
                    )
                    const updateModel = updateFunction(
                        name,
                        value,
                        state.updateModel,
                        state.workingModel
                    )
                    const workingModel = state.workingModel.set(name, value)
                    return {
                        ...state,
                        workingModel,
                        updateModel,
                        canCancel: that._canCancel(
                            updateModel,
                            state.imageToUpload,
                            workingModel
                        ),
                        canSave: that._canSave(
                            updateModel,
                            state.imageToUpload,
                            workingModel
                        ),
                    }
                }
                case that._actionsToHandle.APPLY_MODEL_UPDATE:
                    return {
                        ...state,
                        isSavingItem: true,
                        canCancel: false,
                        canSave: false,
                        canDelete: false,
                    }
                case that._actionsToHandle.CANCEL_MODEL_UPDATE:
                    return {
                        ...state,
                        isEditMode: false,
                        isCreateMode: false,
                    }
                case that._actionsToHandle.CLEAR_FILTER_CRITERIA:
                    return {
                        ...state,
                        searchCriteria: that._defaultState.searchCriteria || "",
                        workingFilterModel:
                            that._defaultState.workingFilterModel ||
                            new that._workingFilterModelClass(),
                        searchFilterModel: new Map(),
                    }
                case that._actionsToHandle.UPDATE_FILTER_CRITERIA:
                    searchFilterModel = that._updateSearchFilterModel(
                        name,
                        value,
                        searchFilterModel,
                        workingFilterModel
                    )
                    return {
                        ...state,
                        searchCriteria: serialiseSearchCriteria(
                            searchFilterModel
                        ),
                        searchFilterModel,
                        workingFilterModel: workingFilterModel.set(name, value),
                    }
                case that._actionsToHandle.LIST_SEARCH_REQUEST:
                    searchFilterModel = that._updateSearchFilterModel(
                        name,
                        value,
                        searchFilterModel,
                        workingFilterModel
                    )
                    return {
                        ...state,
                        searchCriteria: serialiseSearchCriteria(
                            searchFilterModel
                        ),
                        searchFilterModel,
                        workingFilterModel: workingFilterModel.set(name, value),
                        isFetchingList: true,
                    }
                case that._actionsToHandle.SET_FILTER:
                    const { isFilterActive } = action
                    return {
                        ...state,
                        isFilterActive,
                    }
                case that._actionsToHandle.SET_EDIT_MODE:
                    const { isEditMode } = action
                    return {
                        ...state,
                        isEditMode,
                        canCancel: isEditMode || state.canCancel,
                    }
                case that._actionsToHandle.ITEM_REQUEST:
                    const { id, isInvalidating } = action
                    return {
                        ...state,
                        workingModel:
                            that._defaultState.workingModel ||
                            that._workingModelClass(),
                        isFetchingItem: true,
                        isInvalidating,
                        activeId: id,
                        error: null,
                    }
                case that._actionsToHandle.ITEM_CACHED_RESPONSE:
                case that._actionsToHandle.ITEM_SAVE_RESPONSE:
                case that._actionsToHandle.ITEM_RESPONSE: {
                    const { error, data } = action
                    const workingModel = that._workingModelToUI(
                        new that._workingModelClass(data)
                    )
                    const updateModel = new Map()
                    return {
                        ...state,
                        activeId: data && data.id,
                        isFetchingItem: false,
                        isSavingItem: false,
                        isEditMode: false,
                        isCreateMode: false,
                        workingModel,
                        updateModel,
                        imageToUpload: undefined,
                        canCancel: that._canCancel(
                            updateModel,
                            undefined,
                            workingModel
                        ),
                        canSave: that._canSave(
                            updateModel,
                            undefined,
                            workingModel
                        ),
                        canDelete: that._canDelete(workingModel),
                        error: error ? "Error Message" : null,
                    }
                }
                case that._actionsToHandle.ITEM_DELETE_ERROR_RESPONSE:
                case that._actionsToHandle.ITEM_SAVE_ERROR_RESPONSE:
                case that._actionsToHandle.ITEM_ERROR_RESPONSE: {
                    const { workingModel, updateModel } = state
                    return {
                        ...state,
                        isFetchingItem: false,
                        isSavingItem: false,
                        isDeletingItem: false,
                        canCancel: that._canCancel(
                            updateModel,
                            undefined,
                            workingModel
                        ),
                        canSave: that._canSave(
                            updateModel,
                            undefined,
                            workingModel
                        ),
                        canDelete: that._canDelete(workingModel),
                        error,
                    }
                }
                case that._actionsToHandle.ITEM_DELETE_REQUEST:
                    return {
                        ...state,
                        isDeletingItem: true,
                        canCancel: false,
                        canSave: false,
                        canDelete: false,
                    }
                case that._actionsToHandle.ITEM_DELETE_RESPONSE:
                    return {
                        ...state,
                        isDeletingItem: false,
                        workingModel:
                            that._defaultState.workingModel ||
                            new that._workingModelClass(),
                        updateModel: new Map(),
                        imageToUpload: undefined,
                        canCancel: false,
                        canSave: false,
                        canDelete: false,
                        error: error ? "Error Message" : null,
                    }
                case that._actionsToHandle.LIST_PAGE_REQUEST:
                    const { searchCriteria } = action
                    return {
                        ...state,
                        isFetchingList: true,
                        searchCriteria: serialiseSearchCriteria(searchCriteria),
                        error: null,
                    }
                case that._actionsToHandle.LIST_CACHED_RESPONSE:
                case that._actionsToHandle.LIST_RESPONSE:
                    return {
                        ...state,
                        isFetchingList: false,
                        error: error ? "Error Message" : null,
                    }
                case that._actionsToHandle.LIST_ERROR_RESPONSE:
                    return {
                        ...state,
                        isFetchingList: false,
                        error,
                    }
                case UNAUTHENTICATED:
                    return that._defaultState
                default:
                    return state
            }
        }
    }
}

export default PresentationReducerBuilder
